import { useEffect, useRef } from "react";

import { Form } from "antd";

import AwesomeDebouncePromise from "awesome-debounce-promise";

import useFormOperations from "@Totum/QuestionnaireBuilder/Application/hooks/useFormOperations";
import {
    isFormValid,
    resetAndPopulateSelectionSet,
    showQuestionnaireUpdatedNotification,
} from "@Totum/QuestionnaireBuilder/Presentation//helpers";
import { IDetailsFormFields } from "@Totum/QuestionnaireBuilder/Presentation//types";
import { isPresent, ITreeItem, usePrevious } from "@Totum/Main/Utils";

/**
 * Presents the `DetailsForm` component with the UI model and handlers.
 */
const useDetailsFormPresenter = () => {
    // ** Hooks ** //
    const [form] = Form.useForm<IDetailsFormFields>();
    const {
        questionnaire,

        activityCategoriesTree,
        categorySets,

        addTreeItem,
        removeTreeItem,
        updateTitleOrDescription,
        updatePoints,
    } = useFormOperations();

    const previousQuestionnaireId = usePrevious(questionnaire.questionnaireId);

    // ** State ** //
    const entitySelectionSetRef = useRef<Set<string>>(new Set());

    // ** Constants & Variables ** //

    // ** Handlers ** //

    /**
     * Handles the selection of an item in the Activity tree
     * @param value The value of the selected item
     * @param option The tree item that was selected
     */
    const handleSelect = async (valueWithPrefix: string, option: ITreeItem) => {
        const operationResult = await addTreeItem(valueWithPrefix, option);

        if (operationResult.hasAction) {
            showQuestionnaireUpdatedNotification(operationResult.isSuccess, questionnaire.title);
        }
    };

    /**
     * Handles the deselection of an item in the Activity tree
     * @param value The value of the deselected item
     * @param option The tree item that was deselected
     */
    const handleDeselect = async (valueWithPrefix: string, option: ITreeItem) => {
        const operationResult = await removeTreeItem(valueWithPrefix, option);

        const { associationCollection } = form.getFieldsValue();

        if (Boolean(associationCollection.length) && operationResult.hasAction) {
            showQuestionnaireUpdatedNotification(operationResult.isSuccess, questionnaire.title);
        }
    };

    const handleValuesChange = async (_changedValues: Partial<IDetailsFormFields>, _values: IDetailsFormFields) => {
        // `handleSelect` & `handleDeselect` handle changes to associationCollection
        if ("associationCollection" in _changedValues) {
            return;
        } else if ("points" in _changedValues) {
            await debouncedUpdatePoints();
            return;
        }
        await debouncedUpdateTitleOrDescription();
    };

    /**
     * Debounced function to update the title or description
     */
    const debouncedUpdateTitleOrDescription = AwesomeDebouncePromise(async () => {
        const { title, description } = form.getFieldsValue();

        const operationResult = await updateTitleOrDescription(title, description);

        const isValid = await isFormValid(form);

        if (!isValid) return undefined;

        const upsertedText = isPresent(questionnaire.questionnaireId) ? "update" : "create";

        if (operationResult.hasAction) {
            showQuestionnaireUpdatedNotification(operationResult.isSuccess, questionnaire.title, upsertedText);
        }
    }, 1500);

    /**
     * Debounced function to update the title or description
     */
    const debouncedUpdatePoints = AwesomeDebouncePromise(async () => {
        const { points } = form.getFieldsValue();

        if (!points) return undefined;

        const isValid = await isFormValid(form);

        if (!isValid) return undefined;

        const operationResult = await updatePoints(points);

        const upsertedText = isPresent(questionnaire.questionnaireId) ? "update" : "create";

        if (operationResult.hasAction) {
            showQuestionnaireUpdatedNotification(operationResult.isSuccess, questionnaire.title, upsertedText);
        }
    }, 1500);

    // ** Effects ** //
    useEffect(() => {
        // reset the form when the id changes
        if (questionnaire.questionnaireId === previousQuestionnaireId) {
            return;
        }

        // clear the selection set and form fields
        resetAndPopulateSelectionSet(questionnaire, form, entitySelectionSetRef, categorySets);
    }, [categorySets, form, previousQuestionnaireId, questionnaire]);

    // ** Exports ** //

    return {
        handlers: {
            handleDeselect,
            handleSelect,
            handleValuesChange,
        },

        uiModel: {
            questionnaire: {
                isPublished: questionnaire?.isPublished,
            },
            activityCategoriesTree,
            form,
        },
    };
};

export default useDetailsFormPresenter;
